import { List } from 'immutable';
import type { Action } from 'redux';
import type { RequestSagaAction } from '~/types/actions.types';
import type { FilterTypes, SavableFilterTypes } from './types/filter-value-types';
import type { SavedFilterAPIResponse } from './types/api-reponse-types';
import type { FilterDefinitionType } from './data/definitions';
import type { FilterDefinitionValueTypes } from './reducer/MachineFilterStateRecord';
import SavedFilterRecord from './data/SavedFilterRecord';
import FilterDefinitionValueRecord from './data/FilterDefinitionValueRecord';
import MachineFilterV2Constants from './constants';

export const getFilterDefinitions = () => ({
  type: MachineFilterV2Constants.GET_FILTER_DEFINITIONS,
});

export const getSavedFilters = () => ({
  type: MachineFilterV2Constants.GET_SAVED_FILTERS,
});

export const addFilterValue = (
  filterDefinition: FilterDefinitionType,
  filterValue: FilterDefinitionValueTypes
) => ({
  type: MachineFilterV2Constants.ADD_FILTER_VALUE,
  filterDefinition,
  filterValue,
});

export const removeFilterValue = ({
  filterName,
  filterValue,
}: {
  filterName: string;
  filterValue?: FilterDefinitionValueRecord;
}) => ({
  type: MachineFilterV2Constants.REMOVE_FILTER_VALUE,
  filterName,
  filterValue,
});

export const clearFilterValuesByName = (filterName: string) => ({
  type: MachineFilterV2Constants.CLEAR_FILTER_VALUES_BY_NAME,
  filterName,
});

export const clearAllFilterValues = () => ({
  type: MachineFilterV2Constants.CLEAR_ALL_FILTER_VALUES,
});

export const setFilterValues = (filters: List<FilterTypes>) => ({
  type: MachineFilterV2Constants.SET_FILTER_VALUES,
  filters,
});

export const applySavedFilter = (savedFilter: SavedFilterRecord) => ({
  type: MachineFilterV2Constants.APPLY_SAVED_FILTER,
  savedFilter,
});

export const removeSavedFilter = (filterId: string) => ({
  filterId,
  type: MachineFilterV2Constants.REMOVE_SAVED_FILTER,
});

export const setVisibilityFilter = (assetIds: AssetId[]) => ({
  assetIds,
  type: MachineFilterV2Constants.SET_VISIBILITY_FILTER,
});

export const clearVisibilityFilter = () => ({
  type: MachineFilterV2Constants.CLEAR_VISIBILITY_FILTER,
});

export type MachineFilterV2Actions =
  | (Action<MachineFilterV2Constants.ADD_FILTER_VALUE> & {
      filterDefinition: FilterDefinitionType;
      filterValue: FilterDefinitionValueTypes;
    })
  | (Action<MachineFilterV2Constants.REMOVE_FILTER_VALUE> & {
      filterName: string;
      filterValue?: FilterDefinitionValueRecord;
    })
  | (Action<MachineFilterV2Constants.CLEAR_FILTER_VALUES_BY_NAME> & { filterName: string })
  | Action<MachineFilterV2Constants.CLEAR_ALL_FILTER_VALUES>
  | (Action<MachineFilterV2Constants.SET_FILTER_VALUES> & { filters: List<SavableFilterTypes> })
  | (Action<MachineFilterV2Constants.APPLY_SAVED_FILTER> & { savedFilter: SavedFilterRecord })
  | (Action<MachineFilterV2Constants.SET_VISIBILITY_FILTER> & { assetIds: string[] })
  | Action<MachineFilterV2Constants.CLEAR_VISIBILITY_FILTER>
  | RequestSagaAction<
      MachineFilterV2Constants.GET_FILTER_DEFINITIONS_REQUEST,
      MachineFilterV2Constants.GET_FILTER_DEFINITIONS_REQUEST_SUCCESS,
      MachineFilterV2Constants.GET_FILTER_DEFINITIONS_REQUEST_ERROR,
      Record<string, never>,
      { filterSections: unknown[] }
    >
  | RequestSagaAction<
      MachineFilterV2Constants.GET_SAVED_FILTERS_REQUEST,
      MachineFilterV2Constants.GET_SAVED_FILTERS_REQUEST_SUCCESS,
      MachineFilterV2Constants.GET_SAVED_FILTERS_REQUEST_ERROR,
      Record<string, never>,
      SavedFilterAPIResponse[]
    >
  | RequestSagaAction<
      MachineFilterV2Constants.SAVE_NEW_FILTER_REQUEST,
      MachineFilterV2Constants.SAVE_NEW_FILTER_REQUEST_SUCCESS,
      MachineFilterV2Constants.SAVE_NEW_FILTER_REQUEST_ERROR,
      Record<string, never>,
      SavedFilterAPIResponse
    >
  | RequestSagaAction<
      MachineFilterV2Constants.EDIT_SAVED_FILTER_REQUEST,
      MachineFilterV2Constants.EDIT_SAVED_FILTER_REQUEST_SUCCESS,
      MachineFilterV2Constants.EDIT_SAVED_FILTER_REQUEST_ERROR,
      { filterId: string; favorite: boolean },
      SavedFilterAPIResponse
    >
  | RequestSagaAction<
      MachineFilterV2Constants.REMOVE_SAVED_FILTER_REQUEST,
      MachineFilterV2Constants.REMOVE_SAVED_FILTER_REQUEST_SUCCESS,
      MachineFilterV2Constants.REMOVE_SAVED_FILTER_REQUEST_ERROR,
      { filterId: string },
      Record<string, never>
    >;
